"use client"

/**
 * Consumer banner entry — re-exports SystemBanner from the package and ships a
 * LocalBanner with `actionPosition` (inline actions on the right by default).
 */

export { SystemBanner } from "@exxatdesignux/ui/components/banner"
export type { SystemBannerProps } from "@exxatdesignux/ui/components/banner"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Tip } from "@/components/ui/tip"

const VARIANT_CONFIG = {
  info: {
    icon: "fa-circle-info",
    prefix: "fa-light",
    role: "status" as const,
    live: "polite" as const,
  },
  warning: {
    icon: "fa-triangle-exclamation",
    prefix: "fa-light",
    role: "alert" as const,
    live: "assertive" as const,
  },
  error: {
    icon: "fa-circle-exclamation",
    prefix: "fa-light",
    role: "alert" as const,
    live: "assertive" as const,
  },
  success: {
    icon: "fa-circle-check",
    prefix: "fa-light",
    role: "status" as const,
    live: "polite" as const,
  },
  promo: {
    icon: "fa-star-christmas text-brand",
    prefix: "fa-duotone fa-solid",
    role: "status" as const,
    live: "polite" as const,
  },
}

const localBannerVariants = cva(
  "flex gap-3 rounded-lg border px-4 py-3 text-sm transition-[color,background-color,border-color] duration-150 ease-out",
  {
    variants: {
      variant: {
        info: "border-chip-1/30 bg-chip-1/5 text-foreground dark:bg-chip-1/10 dark:border-chip-1/20",
        warning: "border-chip-4/30 bg-chip-4/5 text-foreground dark:bg-chip-4/10 dark:border-chip-4/20",
        error: "border-destructive/30 bg-destructive/5 text-foreground dark:bg-destructive/10 dark:border-destructive/20",
        success: "border-chip-2/30 bg-chip-2/5 text-foreground dark:bg-chip-2/10 dark:border-chip-2/20",
        promo: "border-brand/30 bg-brand/5 text-foreground dark:bg-brand/10 dark:border-brand/20",
      },
      actionPosition: {
        inline: "flex-nowrap",
        bottom: "",
      },
    },
    defaultVariants: { variant: "info", actionPosition: "inline" },
  },
)

export interface LocalBannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof localBannerVariants> {
  title?: string
  children: React.ReactNode
  dismissible?: boolean
  onDismiss?: () => void
  action?: {
    label: string
    href?: string
    onClick?: () => void
  }
  retry?: {
    label?: string
    onClick: () => void
  }
  icon?: string
  /** Custom inline control on the right (e.g. Ask Leo). */
  trailing?: React.ReactNode
  /** Inline puts action/retry on the right; bottom stacks under copy. Default inline. */
  actionPosition?: "inline" | "bottom"
}

function LocalBannerActions({
  action,
  retry,
}: {
  action?: LocalBannerProps["action"]
  retry?: LocalBannerProps["retry"]
}) {
  if (!action && !retry) return null
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {retry ? (
        <Button size="xs" variant="outline" onClick={retry.onClick}>
          <i className="fa-light fa-arrow-rotate-right text-xs" aria-hidden="true" />
          {retry.label ?? "Retry"}
        </Button>
      ) : null}
      {action ? (
        action.href ? (
          <a
            href={action.href}
            className="inline-flex items-center gap-1 text-xs font-semibold underline underline-offset-2 hover:no-underline"
          >
            {action.label}{" "}
            <i className="fa-light fa-arrow-right text-xs" aria-hidden="true" />
          </a>
        ) : (
          <Button size="xs" variant="outline" onClick={action.onClick}>
            {action.label}
          </Button>
        )
      ) : null}
    </div>
  )
}

export function LocalBanner({
  children,
  title,
  variant = "info",
  dismissible = false,
  onDismiss,
  action,
  retry,
  icon,
  trailing,
  actionPosition = "inline",
  className,
  ...props
}: LocalBannerProps) {
  const [dismissed, setDismissed] = React.useState(false)
  const config = VARIANT_CONFIG[variant ?? "info"]
  const hasActions = Boolean(action || retry || trailing)

  function handleDismiss() {
    setDismissed(true)
    onDismiss?.()
  }

  if (dismissed) return null

  return (
    <div
      data-slot="local-banner"
      role={config.role}
      aria-live={config.live}
      className={cn(
        localBannerVariants({ variant, actionPosition }),
        title ? "items-start" : "items-center",
        className,
      )}
      {...props}
    >
      <i
        className={cn(
          config.prefix,
          icon ?? config.icon,
          "text-md shrink-0",
          title ? "mt-0.5" : undefined,
        )}
        aria-hidden="true"
      />

      {actionPosition === "inline" ? (
        <>
          <div className="min-w-0 flex-1">
            {title ? <p className="font-semibold leading-tight mb-0.5">{title}</p> : null}
            <div className="text-sm leading-relaxed opacity-90">{children}</div>
          </div>
          {hasActions ? (
            <div className="ms-auto flex shrink-0 flex-wrap items-center justify-end gap-2 self-start">
              <LocalBannerActions action={action} retry={retry} />
              {trailing}
            </div>
          ) : null}
        </>
      ) : (
        <div className="min-w-0 flex-1">
          {title ? <p className="font-semibold leading-tight mb-0.5">{title}</p> : null}
          <div className="text-sm leading-relaxed opacity-90">{children}</div>
          {hasActions ? (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <LocalBannerActions action={action} retry={retry} />
              {trailing}
            </div>
          ) : null}
        </div>
      )}

      {dismissible ? (
        <Tip label="Dismiss" side="left">
          <button
            type="button"
            aria-label="Dismiss"
            onClick={handleDismiss}
            className={cn(
              "inline-flex size-8 shrink-0 items-center justify-center rounded transition-colors",
              "hover:bg-current/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <i className="fa-light fa-xmark text-xs" aria-hidden="true" />
          </button>
        </Tip>
      ) : null}
    </div>
  )
}
